import _ from "lodash";
import moment from "moment";

import { CalendarTime } from "../../../components/atoms/CalendarTimeButton/interface";
import { RepairShop } from "../../../model/RepairShop";
import { RepairShopBusiness } from "../../../model/RepairShopBusiness";
import { RepairShopBusinessBreakTimes } from "../../../model/RepairShopBusinessBreakTimes";
import { RepairShopDayOffs } from "../../../model/RepairShopDayOffs";
import { RepairShopTemporaryChangedBusiness } from "../../../model/RepairShopTemporaryChangedBusiness";
import sharedConstants from "../../../shared/constants";
import {
  BusinessDayType,
  DayOffDay,
  DayOffWeek,
  ReservationInterval,
  RestDayType,
  RoutineType,
  TemporaryBusinessType,
} from "../../../shared/enums";
import { ValidationStatus } from "../../Register/components/types";
import { EditRepairShopBusinessRequestBody } from "../../../apis/ReservationConfig/apis/types";
import {
  dayOffDays,
  endWeekendTime,
  endWeekTimes,
  holidayTimes,
  initialDayOffDays,
  initialRepairshopBusiness,
  initialRepairshopTemporaryChangeBusinessTime,
  initialWeek,
  repairShopBusinessMomentKeys,
  startWeekendTime,
  startWeekTimes,
  weekendRestDays,
  weekRestDays,
} from "../constants";
import {
  AllDayBreakTime,
  BusinessInfoOfDay,
  CheckDuplicatedBreakTimeArgs,
  ConfigFormValues,
  DayOffTime,
  MakeDisabledTimeArgs,
  MaximumCount,
  NewRepairShopBusiness,
  NotWeeklyDayOff,
  ReservationTime,
  RestTime,
  TimeMode,
} from "../Interface";

interface DateTarget {
  startedAt?: string;
  endedAt?: string;
}
export interface CheckDuplicateDateArgs {
  target: DateTarget;
  times: DateTarget[];
}

export const checkDuplicatedDate = (
  value: CheckDuplicateDateArgs
): ValidationStatus => {
  const { target, times = [] } = value;

  for (let i = 0; i < times.length; i += 1) {
    const time = times[i];
    const { startedAt, endedAt } = time;
    const nowStartDate = moment(startedAt);
    const nowEndDate = moment(endedAt);
    if (
      moment(target.startedAt).format("YYYY/MM/DD") ===
      nowEndDate.format("YYYY/MM/DD")
    )
      return {
        status: "error",
        explain: "",
      };
    if (target.startedAt && target.endedAt && nowStartDate && nowEndDate) {
      if (
        nowStartDate.isBetween(
          moment(target.startedAt),
          moment(target.endedAt)
        ) ||
        nowEndDate.isBetween(moment(target.startedAt), moment(target.endedAt))
      )
        return {
          status: "error",
          explain:
            sharedConstants.message.ALERT_MESSAGE.NO_DUPLICATED_CLOSE_DAY,
        };
      if (
        moment(target.startedAt).isBetween(nowStartDate, nowEndDate) ||
        moment(target.endedAt).isBetween(nowStartDate, nowEndDate)
      ) {
        return {
          status: "error",
          explain:
            sharedConstants.message.ALERT_MESSAGE.NO_DUPLICATED_CLOSE_DAY,
        };
      }
    }
  }

  return { status: undefined, explain: undefined };
};
export const checkDuplicatedBreakTime = (
  value: CheckDuplicatedBreakTimeArgs
): ValidationStatus => {
  const { target, times = [] } = value;

  const newTarget = _.cloneDeep(target);
  const businessTimes = times[times.length - 1];

  if (newTarget.endTime === undefined && newTarget.startTime === undefined)
    return { status: undefined, explain: undefined };
  for (let i = 0; i < times.length; i += 1) {
    const time = times[i];
    const { startTime, endTime } = _.cloneDeep(time);
    if (newTarget.startTime?.format("HH:mm") === startTime?.format("HH:mm"))
      return {
        status: "error",
        explain: "휴게시간은 영업시간 안에 포함되어있어야 합니다.",
      };
    if (newTarget.startTime && newTarget.endTime && startTime && endTime) {
      if (
        !newTarget.startTime?.isBetween(
          businessTimes.startTime,
          businessTimes.endTime
        ) ||
        !newTarget.endTime?.isBetween(
          businessTimes.startTime,
          businessTimes.endTime
        )
      ) {
        return {
          status: "error",
          explain: "휴게시간은 영업시간 안에 포함되어있어야 합니다.",
        };
      }
      if (
        target.endTime?.format("HH:mm") ===
        businessTimes.endTime?.format("HH:mm")
      ) {
        return {
          status: "error",
          explain: "휴게시간이 다른 휴게시간 혹은 영업시간과 중복됩니다",
        };
      }
      if (
        target.startTime?.format("HH:mm") ===
        businessTimes.startTime?.format("HH:mm")
      ) {
        return {
          status: "error",
          explain: "휴게시간이 다른 휴게시간 혹은 영업시간과 중복됩니다",
        };
      }

      const isBefore = newTarget.endTime?.isBefore(startTime)
        ? undefined
        : newTarget.startTime?.isBefore(startTime);
      const isAfter = newTarget.startTime?.isAfter(endTime)
        ? undefined
        : newTarget.endTime?.isAfter(endTime);

      if (isBefore || isAfter)
        return {
          status: "error",
          explain: "휴게시간이 다른 휴게시간 혹은 영업시간과 중복됩니다",
        };
    }
  }

  return { status: undefined, explain: undefined };
};
export const getStandardTime = (
  times: (moment.Moment | undefined)[],
  desc?: boolean
) => {
  if (desc === true)
    times.sort((prev, next) => {
      if (prev && next)
        return prev?.hours() === next?.hours()
          ? next?.minutes() - prev?.minutes()
          : next?.hours() - prev?.hours();
      return 0;
    });
  else
    times.sort((prev, next) => {
      if (prev && next)
        return prev?.hours() === next?.hours()
          ? prev?.minutes() - next?.minutes()
          : prev?.hours() - next?.hours();
      return 0;
    });

  return times[0];
};

export const makeDisabledTime = (value: MakeDisabledTimeArgs) => {
  const {
    fatestStartTime,
    fatestEndTime,
    latestEndTime,
    latestStartTime,
    disabledTimes = [],
    mode = TimeMode.START,
  } = value;

  const returnHours: number[] = [];

  disabledTimes.map((val) => {
    const { endTime, startTime } = val;
    if (endTime && startTime && latestStartTime && fatestEndTime)
      for (let time = startTime.hours(); time < endTime.hours(); time += 1) {
        if (
          !returnHours.includes(time) &&
          time < latestStartTime?.hours() &&
          time > fatestEndTime?.hours()
        )
          returnHours.push(time);
      }
    return null;
  });
  const defaultHours = [
    ...makeDisableStartTime({ endTime: latestEndTime }).disabledHours(),
    ...makeDisableEndTime({ startTime: fatestStartTime }).disabledHours(),
  ];
  switch (mode) {
    case TimeMode.START:
      return {
        disabledHours: () => {
          return [
            ...returnHours,
            ...makeDisableStartTime({
              endTime: fatestEndTime,
            }).disabledHours(),
            ...defaultHours,
          ];
        },
        disabledMinutes: (selectHour: number) => {
          return [
            ...makeDisableStartTime({
              endTime: fatestEndTime,
            }).disabledMinutes(selectHour),
          ];
        },
      };

    case TimeMode.END:
      return {
        disabledHours: () => {
          return [
            ...returnHours,
            ...makeDisableEndTime({
              startTime: latestStartTime,
            }).disabledHours(),
            ...defaultHours,
          ];
        },
        disabledMinutes: (selectHour: number) => {
          return [
            ...makeDisableEndTime({
              startTime: latestStartTime,
            }).disabledMinutes(selectHour),
          ];
        },
      };

    default:
      return { disabledHours: () => [] };
  }
};
// 시작시간 고르는 picker에서 disable될 시간배열을 만드는 함수
export const makeDisableStartTime = (value: {
  endTime?: moment.Moment;
  startTime?: moment.Moment;
}) => {
  const returnHours: number[] = [];
  return {
    disabledHours: () => {
      if (value.endTime) {
        if (value.endTime.minutes() === 30)
          for (let i = 23; i > value.endTime.hour(); i -= 1) {
            returnHours.push(i);
          }
        else
          for (let i = 23; i >= value.endTime.hour(); i -= 1) {
            returnHours.push(i);
          }
      }
      return returnHours;
    },
    disabledMinutes: (selectHour: number) => {
      if (value.endTime?.hours() === selectHour) {
        return [30];
      }
      return [];
    },
  };
};

// 종료시간 고르는 picker에서 disable될 시간배열을 만드는 함수

export const makeDisableEndTime = (value: {
  startTime?: moment.Moment;
  endTime?: moment.Moment;
}) => ({
  disabledHours: () => {
    const returnHours: number[] = [];
    if (value.startTime) {
      if (value.startTime.minutes() === 0)
        for (let i = 0; i < value.startTime.hour(); i += 1) {
          returnHours.push(i);
        }
      else {
        for (let i = 0; i <= value.startTime.hour(); i += 1) {
          returnHours.push(i);
        }
      }
    }
    return returnHours;
  },
  disabledMinutes: (selectHour: number) => {
    if (selectHour === value.startTime?.hours()) {
      return [0];
    }
    return [];
  },
});

// requst body 만드는 함수
export const makeRequestBody = (values: ConfigFormValues, id: number) => {
  const newRequestBody: EditRepairShopBusinessRequestBody = {
    repairShopBusiness: makeRepairShopBusiness(values),

    repairShopTemporaryBusinesses: [...makeRepairShopTemporaryBusiness(values)],
    id,
  };
  return newRequestBody;
};

// formvalue를 requestbody의 RepairShopTemporaryBusiness로 바꾸는 함수
const makeRepairShopTemporaryBusiness = (formValue: ConfigFormValues) => {
  const {
    // 임시 변경일 배열
    temporaryChangeArray = [],
    // 임수 중단 날짜
    temporaryCloseRanges = [],
    // 임시 중단 사유
    temporaryClosedReason = "",
  } = formValue;
  const newTemporaryChangedBusinesses: RepairShopTemporaryChangedBusiness[] =
    [];

  for (let i = 0; i < temporaryChangeArray?.length; i += 1) {
    const nowItem = temporaryChangeArray[i];
    const {
      repairShopBreakTimes = [],
      temporaryBusinessStartedAt,
      temporaryBusinessEndedAt,
      endBusinessTime,
      startBusinessTime,
    } = nowItem;

    // 날짜가 빈값이 아니면
    if (endBusinessTime && startBusinessTime)
      newTemporaryChangedBusinesses.push({
        ...nowItem,
        temporaryBusinessType: TemporaryBusinessType.CHANGED,
        startBusinessTime,
        endBusinessTime,
        temporaryBusinessStartedAt: moment(temporaryBusinessStartedAt)
          .hours(0)
          .minutes(0)
          .seconds(0)
          .toISOString(),
        temporaryBusinessEndedAt: moment(temporaryBusinessEndedAt)
          .hours(23)
          .minutes(59)
          .seconds(59)
          .toISOString(),
        repairShopBreakTimes: repairShopBreakTimes
          .filter(
            (val) =>
              val.endBreakTime !== undefined || val.startBreakTime !== undefined
          )
          .map((val) => ({ ...val, breakDayTypes: DayOffDay.SUNDAY })),
      });
  }

  // 임시중단날짜가 잇으면
  if (temporaryCloseRanges?.filter((val) => val?.length === 2)?.length > 0) {
    for (
      let i = 0;
      i < temporaryCloseRanges?.filter((val) => val?.length === 2)?.length;
      i += 1
    ) {
      const temporaryCloseRange = temporaryCloseRanges.filter(
        (val) => val?.length === 2
      )[i];
      newTemporaryChangedBusinesses.push({
        temporaryBusinessStartedAt: temporaryCloseRange[0]
          .hours(0)
          .minutes(0)
          .seconds(0)
          .toISOString(),
        temporaryBusinessEndedAt: temporaryCloseRange[1]
          .hours(23)
          .minutes(59)
          .seconds(59)
          .toISOString(),
        temporaryClosedReason,

        temporaryBusinessType: TemporaryBusinessType.CLOSED,
      });
    }
  }

  return newTemporaryChangedBusinesses;
};

// form value를 repairshop business 로 바꿈
const makeRepairShopBusiness = (
  formValue: ConfigFormValues
): RepairShopBusiness => {
  const {
    businessDayTypes,
    isWorkAtHoliday,
    maximumCounts = [],
    repairShopBusiness = getInitialRepairShopBusiness(),
    isDaySelectOn = false,
  } = formValue;

  const requestBody: RepairShopBusiness = {
    ...repairShopBusiness,

    mondayEndBusinessTime: null,
    mondayStartBusinessTime: null,
    tuesdayStartBusinessTime: null,
    tuesdayEndBusinessTime: null,
    wednesdayEndBusinessTime: null,
    wednesdayStartBusinessTime: null,
    thursdayEndBusinessTime: null,
    thursdayStartBusinessTime: null,
    fridayEndBusinessTime: null,
    fridayStartBusinessTime: null,
    saturdayEndBusinessTime: null,
    saturdayStartBusinessTime: null,
    sundayEndBusinessTime: null,
    sundayStartBusinessTime: null,

    businessDayTypes,
    repairShopDayOffs: makeRepairShopDayOffs(formValue),
    repairShopBreakTimes: makeRepairShopBusinessBreakTimes(formValue),
  };

  requestBody.mondayMaximumMorningVehicleCount = null;
  requestBody.mondayMaximumAfternoonVehicleCount = null;
  requestBody.tuesdayMaximumMorningVehicleCount = null;
  requestBody.tuesdayMaximumAfternoonVehicleCount = null;
  requestBody.wednesdayMaximumMorningVehicleCount = null;
  requestBody.wednesdayMaximumAfternoonVehicleCount = null;
  requestBody.thursdayMaximumMorningVehicleCount = null;
  requestBody.thursdayMaximumAfternoonVehicleCount = null;
  requestBody.fridayMaximumMorningVehicleCount = null;
  requestBody.fridayMaximumAfternoonVehicleCount = null;
  requestBody.saturdayMaximumMorningVehicleCount = null;
  requestBody.saturdayMaximumAfternoonVehicleCount = null;
  requestBody.sundayMaximumMorningVehicleCount = null;
  requestBody.sundayMaximumAfternoonVehicleCount = null;
  if (isDaySelectOn) {
    for (let i = 0; i < maximumCounts.length; i += 1) {
      const nowCounts = maximumCounts[i];
      for (let j = 0; j < nowCounts.days.length; j += 1) {
        const nowDay = nowCounts.days[j];
        const morningCount = nowCounts.maximumCounts[0];
        const afternoonCount = nowCounts.maximumCounts[1];
        switch (nowDay) {
          case DayOffDay.MONDAY:
            requestBody.mondayMaximumMorningVehicleCount = morningCount;
            requestBody.mondayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          case DayOffDay.TUESDAY:
            requestBody.tuesdayMaximumMorningVehicleCount = morningCount;
            requestBody.tuesdayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          case DayOffDay.WEDNESDAY:
            requestBody.wednesdayMaximumMorningVehicleCount = morningCount;
            requestBody.wednesdayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          case DayOffDay.THURSDAY:
            requestBody.thursdayMaximumMorningVehicleCount = morningCount;
            requestBody.thursdayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          case DayOffDay.FRIDAY:
            requestBody.fridayMaximumMorningVehicleCount = morningCount;
            requestBody.fridayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          case DayOffDay.SATURDAY:
            requestBody.saturdayMaximumMorningVehicleCount = morningCount;
            requestBody.saturdayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          case DayOffDay.SUNDAY:
            requestBody.sundayMaximumMorningVehicleCount = morningCount;
            requestBody.sundayMaximumAfternoonVehicleCount = afternoonCount;
            break;
          default:
            break;
        }
      }
    }
  }

  if (!isWorkAtHoliday) {
    requestBody.holidayStartBusinessTime = null;
    requestBody.holidayEndBusinessTime = null;
    requestBody.repairShopHolidayWorkdays = [];
  }

  switch (businessDayTypes) {
    case BusinessDayType.EVERYDAY:
      return makeEverydayBusinessTime(formValue, requestBody);
    case BusinessDayType.WEEKDAY_WEEKEND:
      return makeWeekAndWeekendBusinessTime(formValue, requestBody);
    case BusinessDayType.DAY:
      return makeDayBusinessTime(formValue, requestBody);
    default:
      return requestBody;
  }
};

// 휴식시간 배열 만드느  함수
const makeRepairShopBusinessBreakTimes = (
  formValue: ConfigFormValues
): RepairShopBusinessBreakTimes[] => {
  const {
    isEveryDayRestSame,
    isWorkAtHoliday,
    holidayRestTimes = { day: RestDayType.Holiday, times: [] },
    // 매일 같은 날 쉴경우의 시간배열
    everyDaySameRestTimeArray = [],
    // 매일 같은 날 안쉴경우의 시간배열
    notEveryDaySameRestTimeArray = [],
    weekdayWeekendTimeArray = [],
    alldayTimeArray = [],
    alldayBreakTimeArray = [],
    businessDayTypes,
  } = formValue;
  const request: RepairShopBusinessBreakTimes[] = [];

  // 휴식시간 타입을 판별함
  const breakTimeTypes = isEveryDayRestSame;

  let isNoWeekend = false;
  let isNoWeek = false;
  switch (businessDayTypes) {
    case BusinessDayType.EVERYDAY: {
      break;
    }

    case BusinessDayType.WEEKDAY_WEEKEND: {
      isNoWeekend =
        !weekdayWeekendTimeArray[0]?.startTime ||
        !weekdayWeekendTimeArray[1]?.endTime;
      isNoWeek =
        !weekdayWeekendTimeArray[0]?.startTime ||
        !weekdayWeekendTimeArray[0]?.endTime;
      break;
    }
    case BusinessDayType.DAY: {
      isNoWeek =
        alldayTimeArray
          ?.reduce((pre: string[], cur: ReservationTime) => {
            if (cur.days)
              for (let i = 0; i < cur?.days?.length; i += 1) {
                if (!pre.includes(cur.days[i])) pre.push(cur.days[i]);
              }

            return pre;
          }, [])
          .filter(
            (val) =>
              val === DayOffDay.MONDAY ||
              val === DayOffDay.THURSDAY ||
              val === DayOffDay.TUESDAY ||
              val === DayOffDay.WEDNESDAY ||
              val === DayOffDay.FRIDAY
          )?.length === 0;

      isNoWeekend =
        alldayTimeArray
          ?.reduce((pre: string[], cur: ReservationTime) => {
            if (cur.days)
              for (let i = 0; i < cur?.days?.length; i += 1) {
                if (!pre.includes(cur.days[i])) pre.push(cur.days[i]);
              }

            return pre;
          }, [])
          .filter(
            (val) => val === DayOffDay.SATURDAY || val === DayOffDay.SUNDAY
          )?.length === 0;
      break;
    }
    default: {
      break;
    }
  }

  // 매일 같은 시간에 쉴경우/아닐 경우 나눠서 판단함
  switch (isEveryDayRestSame) {
    case BusinessDayType.EVERYDAY:
      for (let i = 0; i < everyDaySameRestTimeArray.length; i += 1) {
        const { startTime, endTime } = everyDaySameRestTimeArray[i];
        if (startTime && endTime) {
          for (let j = 0; j < weekRestDays.length; j += 1) {
            request.push({
              breakDayTypes: weekRestDays[j],
              breakTimeTypes,
              startBreakTime: startTime?.format("HH:mm"),
              endBreakTime: endTime?.format("HH:mm"),
            });
          }
          for (let j = 0; j < weekendRestDays.length; j += 1) {
            request.push({
              breakDayTypes: weekendRestDays[j],
              startBreakTime: startTime?.format("HH:mm"),
              endBreakTime: endTime?.format("HH:mm"),
              breakTimeTypes,
            });
          }
        }
      }
      break;
    case BusinessDayType.WEEKDAY_WEEKEND:
      for (let i = 0; i < notEveryDaySameRestTimeArray?.length; i += 1) {
        const { day } = notEveryDaySameRestTimeArray[i];
        const { times } = notEveryDaySameRestTimeArray[i];
        for (let j = 0; j < times.length; j += 1) {
          const { startTime = undefined, endTime = undefined } = times[j];
          if (startTime && endTime)
            switch (day) {
              case RestDayType.Week:
                if (!isNoWeek)
                  for (
                    let dayIndex = 0;
                    dayIndex < weekRestDays.length;
                    dayIndex += 1
                  ) {
                    request.push({
                      breakDayTypes: weekRestDays[dayIndex],
                      startBreakTime: startTime?.format("HH:mm"),
                      endBreakTime: endTime?.format("HH:mm"),
                      breakTimeTypes,
                    });
                  }

                break;
              case RestDayType.Weekend:
                if (!isNoWeekend)
                  for (
                    let dayIndex = 0;
                    dayIndex < weekendRestDays.length;
                    dayIndex += 1
                  ) {
                    request.push({
                      breakDayTypes: weekendRestDays[dayIndex],
                      startBreakTime: startTime?.format("HH:mm"),
                      endBreakTime: endTime?.format("HH:mm"),
                      breakTimeTypes,
                    });
                  }
                break;
              default:
                for (
                  let dayIndex = 0;
                  dayIndex < weekRestDays.length;
                  dayIndex += 1
                ) {
                  request.push({
                    breakDayTypes: weekRestDays[dayIndex],
                    startBreakTime: startTime?.format("HH:mm"),
                    endBreakTime: endTime?.format("HH:mm"),
                    breakTimeTypes,
                  });
                }
                break;
            }
        }
      }

      break;
    case BusinessDayType.DAY: {
      const allday = alldayTimeArray?.reduce(
        (pre: string[], cur: ReservationTime) => {
          if (cur.days)
            for (let i = 0; i < cur?.days?.length; i += 1) {
              if (!pre.includes(cur.days[i])) pre.push(cur.days[i]);
            }

          return pre;
        },
        []
      );

      for (let i = 0; i < alldayBreakTimeArray.length; i += 1) {
        const nowBreakTime = alldayBreakTimeArray[i];

        const { days, breakTimes } = nowBreakTime;

        for (let j = 0; j < days.length; j += 1) {
          for (let q = 0; q < breakTimes.length; q += 1) {
            if (breakTimes[q].startBreakTime && breakTimes[q].endBreakTime)
              if (businessDayTypes === BusinessDayType.DAY) {
                if (allday.includes(days[j])) {
                  request.push({
                    breakDayTypes: days[j],
                    startBreakTime: breakTimes[q].startBreakTime,
                    endBreakTime: breakTimes[q].endBreakTime,
                    breakTimeTypes: BusinessDayType.DAY,
                    groupIndex: i,
                  });
                }
              } else if (days[j] !== DayOffDay.HOLIDAY)
                request.push({
                  breakDayTypes: days[j],
                  startBreakTime: breakTimes[q].startBreakTime,
                  endBreakTime: breakTimes[q].endBreakTime,
                  breakTimeTypes: BusinessDayType.DAY,
                  groupIndex: i,
                });
          }
        }
      }

      break;
    }
    default:
      break;
  }

  // 만든 공휴일 시간 배열을 바디에 넣음
  if (isWorkAtHoliday)
    for (let i = 0; i < holidayRestTimes.times.length; i += 1) {
      const holidayRestTime = holidayRestTimes.times[i];
      const { startTime, endTime } = holidayRestTime;
      if (startTime && endTime)
        request.push({
          breakDayTypes: DayOffDay.HOLIDAY,
          startBreakTime: startTime.format("HH:mm"),
          endBreakTime: endTime.format("HH:mm"),
        });
    }

  return request;
};

// 정기휴무일 배열을 바디에 만드는 함수
const makeRepairShopDayOffs = (
  formValue: ConfigFormValues
): RepairShopDayOffs[] => {
  const request: RepairShopDayOffs[] = [];
  const {
    hasRoutineRestDay,
    routineDayOffType,
    notWeeklyDayOffs = [],
    weeklyDayOffs = [],
  } = formValue;
  // 정기적으로 쉬는지 안쉬는지로 따짐
  if (hasRoutineRestDay) {
    switch (routineDayOffType) {
      case RoutineType.EVERY_WEEK:
        for (let i = 0; i < weeklyDayOffs.length; i += 1) {
          request.push({
            dayOffDay: weeklyDayOffs[i].dayOffDay,
            dayOffWeek: DayOffWeek.EVERY_WEEK,
          });
        }
        break;
      case RoutineType.NOT_EVERY_WEEK:
        for (let i = 0; i < notWeeklyDayOffs.length; i += 1) {
          if (notWeeklyDayOffs[i]) {
            const { dayOffDays = [], dayOffWeek } = notWeeklyDayOffs[i];
            for (let j = 0; j < dayOffDays?.length; j += 1) {
              if (dayOffWeek)
                request.push({
                  dayOffDay: dayOffDays[j],
                  dayOffWeek,
                });
            }
          }
        }
        break;
      default:
        break;
    }
  }

  return request;
};

// 아무것도 설정하지 않았을 때 초기 시간을 리턴하는 함수
export const getInitialRepairShopBusiness = () => {
  const newRepairShopBusiness: NewRepairShopBusiness = {
    maximumSimultaneouslyVehicleCount: 0,
    maximumMorningVehicleCount: 0,
    maximumAfternoonVehicleCount: 0,
    reservationInterval: ReservationInterval.SIXTY,
    mondayStartBusinessTime: "",
    mondayEndBusinessTime: "",
    tuesdayStartBusinessTime: "",
    tuesdayEndBusinessTime: "",
    wednesdayStartBusinessTime: "",
    wednesdayEndBusinessTime: "",
    thursdayStartBusinessTime: "",
    thursdayEndBusinessTime: "",
    fridayStartBusinessTime: "",
    fridayEndBusinessTime: "",
    saturdayStartBusinessTime: "",
    saturdayEndBusinessTime: "",
    sundayStartBusinessTime: "",
    sundayEndBusinessTime: "",
    holidayStartBusinessTime: "",
    holidayEndBusinessTime: "",
    mondayMaximumAfternoonVehicleCount: null,
    mondayMaximumMorningVehicleCount: null,
    tuesdayMaximumAfternoonVehicleCount: null,
    tuesdayMaximumMorningVehicleCount: null,
    wednesdayMaximumAfternoonVehicleCount: null,
    wednesdayMaximumMorningVehicleCount: null,
    thursdayMaximumAfternoonVehicleCount: null,
    thursdayMaximumMorningVehicleCount: null,
    fridayMaximumAfternoonVehicleCount: null,
    fridayMaximumMorningVehicleCount: null,
    saturdayMaximumAfternoonVehicleCount: null,
    saturdayMaximumMorningVehicleCount: null,
    sundayMaximumAfternoonVehicleCount: null,
    sundayMaximumMorningVehicleCount: null,
  };
  return newRepairShopBusiness;
};

// 서버에서 값을 받아와서 정의한 formvalue로 바꾸는 함수
export const getConfigFormValues = (
  repairShop: RepairShop | null
): ConfigFormValues => {
  const repairShopBusiness = changeRepairShopValueToFormValue(
    repairShop === null
      ? getInitialRepairShopBusiness()
      : repairShop.repairShopBusiness
  );
  const { businessDayTypes = BusinessDayType.DAY } = repairShopBusiness;

  // repairShopTemporary 값을 가져옴 없을 경우 매장정보가 없을 경우 초기 값으로 바꿈
  const repairShopTemporaryBusinessesArray: RepairShopTemporaryChangedBusiness[] =
    repairShop === null
      ? []
      : repairShop.repairShopTemporaryBusinesses?.length > 0
      ? repairShop.repairShopTemporaryBusinesses
      : [];
  const configFormValues: ConfigFormValues = {
    repairShopBusiness,
    repairShopTemporaryBusinesses: repairShopTemporaryBusinessesArray.map(
      (value: RepairShopTemporaryChangedBusiness) => {
        return {
          ...value,
          temporaryBusinessChangeRange: [
            moment(value.temporaryBusinessStartedAt).hours(0).minutes(0),
            moment(value.temporaryBusinessEndedAt).hours(23).minutes(59),
          ],
        };
      }
    ),

    businessDayTypes,
    // 최대 장착 가능 차량 수
    ...getInitialRepairShopMaximumCounts(repairShop),
    // 정기휴무일 배열 만듬
    ...getInitialDayOffDays(repairShopBusiness),
    // 휴식시간 배열만듬
    ...getInitialRestTime(repairShopBusiness),
    // 영업시간 배열만듬
    ...getInitialBusinessTime(repairShopBusiness),
    // 임시 영업시간 상태만듬
    ...getInitialTemporaryBusinesses(repairShopTemporaryBusinessesArray),
  };

  return configFormValues;
};
export const getInitialRepairShopMaximumCounts = (
  repairShop?: RepairShop | null
) => {
  if (repairShop) {
    const { repairShopBusiness } = repairShop;
    if (repairShopBusiness) {
      const {
        mondayMaximumMorningVehicleCount,
        mondayMaximumAfternoonVehicleCount,
        tuesdayMaximumMorningVehicleCount,
        tuesdayMaximumAfternoonVehicleCount,
        wednesdayMaximumMorningVehicleCount,
        wednesdayMaximumAfternoonVehicleCount,
        thursdayMaximumMorningVehicleCount,
        thursdayMaximumAfternoonVehicleCount,
        fridayMaximumMorningVehicleCount,
        fridayMaximumAfternoonVehicleCount,
        saturdayMaximumMorningVehicleCount,
        saturdayMaximumAfternoonVehicleCount,
        sundayMaximumMorningVehicleCount,
        sundayMaximumAfternoonVehicleCount,
      } = repairShopBusiness;

      const maximumMorningCounts = [
        mondayMaximumMorningVehicleCount,
        tuesdayMaximumMorningVehicleCount,
        wednesdayMaximumMorningVehicleCount,
        thursdayMaximumMorningVehicleCount,
        fridayMaximumMorningVehicleCount,
        saturdayMaximumMorningVehicleCount,
        sundayMaximumMorningVehicleCount,
      ];
      const maximumAfternoonCounts = [
        mondayMaximumAfternoonVehicleCount,
        tuesdayMaximumAfternoonVehicleCount,
        wednesdayMaximumAfternoonVehicleCount,
        thursdayMaximumAfternoonVehicleCount,
        fridayMaximumAfternoonVehicleCount,
        saturdayMaximumAfternoonVehicleCount,
        sundayMaximumAfternoonVehicleCount,
      ];
      const saves: number[][] = [];
      const days = [
        DayOffDay.MONDAY,
        DayOffDay.TUESDAY,
        DayOffDay.WEDNESDAY,
        DayOffDay.THURSDAY,
        DayOffDay.FRIDAY,
        DayOffDay.SATURDAY,
        DayOffDay.SUNDAY,
      ];
      const result: MaximumCount[] = [];
      for (let i = 0; i < maximumMorningCounts.length; i += 1) {
        const morningCount = maximumMorningCounts[i];
        const afternoonCount = maximumAfternoonCounts[i];
        if (
          morningCount !== undefined &&
          morningCount !== null &&
          afternoonCount !== null &&
          afternoonCount !== undefined
        )
          if (
            !saves.find(
              (val) =>
                val[0] === maximumMorningCounts[i] &&
                val[1] === maximumAfternoonCounts[i]
            )
          ) {
            saves.push([morningCount, afternoonCount]);
            result.push({
              days: [],
              key: `maximum${i}`,
              maximumCounts: [morningCount || 0, afternoonCount || 0],
            });
          }
      }

      for (let i = 0; i < saves.length; i += 1) {
        const nowResult = result[i];
        for (let j = 0; j < maximumAfternoonCounts.length; j += 1) {
          const maximumMorning = maximumMorningCounts[j];
          const maximumAfternoon = maximumAfternoonCounts[j];
          if (
            saves[i][0] === maximumMorning &&
            saves[i][1] === maximumAfternoon
          ) {
            if (!nowResult.days.includes(days[j])) nowResult.days.push(days[j]);
          }
        }
      }

      return {
        maximumCounts:
          result.length > 0
            ? result
            : [
                {
                  key: `maximum${0}`,
                  days: [],
                  maximumCounts: [0, 0],
                },
              ],
        isDaySelectOn: result.length > 0,
      };
    }
  }
  return {
    maximumCounts: [
      {
        key: `maximum${0}`,
        days: [],
        maximumCounts: [0, 0],
      },
    ],
    isDaySelectOn: false,
  };
};
// 임시 영업시간 만드는 함수
export const getInitialTemporaryBusinesses = (
  repairShopTemporaryBusinesses: RepairShopTemporaryChangedBusiness[]
) => {
  const temporaryClosedBusiness = repairShopTemporaryBusinesses.filter(
    (val: RepairShopTemporaryChangedBusiness) => {
      return val.temporaryBusinessType === TemporaryBusinessType.CLOSED;
    }
  );

  // 임시 영업 바꾼 시간
  const temporaryChangeArray = repairShopTemporaryBusinesses
    .filter((val: RepairShopTemporaryChangedBusiness) => {
      return val.temporaryBusinessType === TemporaryBusinessType.CHANGED;
    })
    .map((val) => {
      if (val)
        return {
          ...val,
          temporaryBusinessChangeRange: [
            moment(val?.temporaryBusinessStartedAt),
            moment(val?.temporaryBusinessEndedAt),
          ],
        };
      return {};
    });
  // 임시 중단 영업
  const temporaryCloseRanges: moment.Moment[][] = temporaryClosedBusiness
    ? temporaryClosedBusiness.map((val) => [
        moment(val?.temporaryBusinessStartedAt).hours(0).minutes(0),
        moment(val?.temporaryBusinessEndedAt).hours(23).minutes(59),
      ])
    : [[]];

  const configFormValues: ConfigFormValues = {
    temporaryCloseRanges,
    temporaryClosedReason:
      temporaryClosedBusiness && temporaryClosedBusiness[0]
        ? temporaryClosedBusiness[0].temporaryClosedReason
        : "",

    temporaryChangeArray:
      temporaryChangeArray.length > 0
        ? temporaryChangeArray
        : [initialRepairshopTemporaryChangeBusinessTime],
  };
  return configFormValues;
};
const getWeek = (week: number) => {
  switch (week) {
    case 1:
      return DayOffWeek.ONE;
    case 2:
      return DayOffWeek.TWO;
    case 3:
      return DayOffWeek.THREE;
    case 4:
      return DayOffWeek.FOUR;
    case 5:
      return DayOffWeek.FIVE;
    default:
      return DayOffWeek.ONE;
  }
};
// 휴무일 배열 만드는 함수
export const getInitialDayOffDays = (
  repairShopBusiness: NewRepairShopBusiness
) => {
  const { repairShopDayOffs = [] } = repairShopBusiness;
  // 휴무일이 매주 쉬는지 아닌지
  let newRepairShopDayOffs = repairShopDayOffs;
  if (
    repairShopDayOffs.find((val) => val.dayOffWeek === DayOffWeek.EVERY_WEEK) &&
    repairShopDayOffs.find((val) => val.dayOffWeek !== DayOffWeek.EVERY_WEEK)
  ) {
    const everyDayRepairShopDayOffs = repairShopDayOffs.filter(
      (val) => val.dayOffWeek === DayOffWeek.EVERY_WEEK
    );
    const noteveryDayRepairShopDayOffs = repairShopDayOffs.filter(
      (val) => val.dayOffWeek !== DayOffWeek.EVERY_WEEK
    );
    for (let i = 0; i < everyDayRepairShopDayOffs.length; i += 1) {
      for (let j = 1; j <= 5; j += 1) {
        noteveryDayRepairShopDayOffs.push({
          dayOffWeek: getWeek(j),
          dayOffDay: repairShopDayOffs.filter(
            (val) => val.dayOffWeek === DayOffWeek.EVERY_WEEK
          )[i].dayOffDay,
        });
      }
    }
    newRepairShopDayOffs = noteveryDayRepairShopDayOffs;
  }
  const routineDayOffType =
    newRepairShopDayOffs.length > 0
      ? newRepairShopDayOffs[0].dayOffWeek === DayOffWeek.EVERY_WEEK
        ? RoutineType.EVERY_WEEK
        : RoutineType.NOT_EVERY_WEEK
      : RoutineType.NOT_EVERY_WEEK;
  // 매주 쉬는 배열
  const weeklyDayOffs =
    routineDayOffType === RoutineType.EVERY_WEEK
      ? newRepairShopDayOffs
      : initialWeek;
  // 매주 안쉬는 배열
  const notWeeklyDayOffs =
    routineDayOffType === RoutineType.NOT_EVERY_WEEK
      ? newRepairShopDayOffs.reduce(
          (prev: NotWeeklyDayOff[], current: RepairShopDayOffs) => {
            if (current.dayOffDay)
              if (current.dayOffWeek) {
                if (prev[Number(current.dayOffWeek) - 1] === undefined) {
                  prev[Number(current.dayOffWeek) - 1] = {
                    dayOffWeek: Number(current.dayOffWeek),
                    dayOffDays: [current.dayOffDay],
                  };
                } else {
                  prev[Number(current.dayOffWeek) - 1].dayOffDays?.push(
                    current.dayOffDay
                  );
                  prev[Number(current.dayOffWeek) - 1].dayOffDays = prev[
                    Number(current.dayOffWeek) - 1
                  ].dayOffDays?.reduce(
                    (dayOffDays: DayOffDay[], currentDay) =>
                      dayOffDays.includes(currentDay)
                        ? dayOffDays
                        : [...dayOffDays, currentDay],
                    []
                  );
                }
              }
            return prev;
          },
          []
        )
      : [
          {
            dayOffWeek: DayOffWeek.ONE,
            dayOffDays: initialDayOffDays,
          },
        ];
  if (notWeeklyDayOffs.length === 0)
    notWeeklyDayOffs.push({
      dayOffWeek: DayOffWeek.ONE,
      dayOffDays: initialDayOffDays,
    });
  // 폼밸류에 담음
  const configFormValues: ConfigFormValues = {
    hasRoutineRestDay: newRepairShopDayOffs.length > 0,
    routineDayOffType,
    weeklyDayOffs,
    notWeeklyDayOffs: notWeeklyDayOffs.filter(
      (val) => val.dayOffWeek !== undefined && val.dayOffDays !== undefined
    ),
  };
  return configFormValues;
};

// 초기 휴식시간 상태 만드는 함수
export const getInitialRestTime = (
  repairShopBusiness: NewRepairShopBusiness
) => {
  // 빈 인풋에 들어갈 값
  const initialTime = { startTime: undefined, endTime: undefined };
  //
  const { repairShopBreakTimes = [], repairShopHolidayWorkdays = [] } =
    repairShopBusiness;

  // 매주 같은 시간에 쉬는지
  const isEveryDayRestSame =
    repairShopBreakTimes?.length > 0
      ? repairShopBreakTimes[0].breakTimeTypes
      : BusinessDayType.EVERYDAY;

  // 공휴일에 쉬는지
  const isWorkAtHoliday = repairShopHolidayWorkdays?.length > 0;

  // 공휴일 휴식시간
  const holidayRestTimes: RestTime = {
    day: RestDayType.Holiday,
    times: [],
  };
  // 초기배열

  // 매주 쉬는 쉬간
  const everyDaySameRestTimeArray: DayOffTime[] = [];

  // 매주 안쉴때 쉬는 시간  주말 1인덱스 주중 0인덱스
  const notEveryDaySameRestTimeArray: RestTime[] = [
    {
      day: RestDayType.Week,
      times: [],
    },
    {
      day: RestDayType.Weekend,
      times: [],
    },
  ];
  const alldayBreakTimeArray: AllDayBreakTime[] = [];

  // 쉬는시간 배열을 열어서 요일을 확인하고 주말인지 주중인지 판단해서 필터링함
  if (repairShopBreakTimes.length > 0) {
    const weekBreakTime = repairShopBreakTimes.reduce(
      (prev: DayOffTime[], next: RepairShopBusinessBreakTimes) => {
        const { startBreakTime, endBreakTime, breakDayTypes } = next;
        if (breakDayTypes === DayOffDay.MONDAY)
          return [
            ...prev,
            {
              startTime: stringTimeToMoment(startBreakTime),
              endTime: stringTimeToMoment(endBreakTime),
            },
          ];
        return prev;
      },
      []
    );

    const weekendBreakTime = repairShopBreakTimes.reduce(
      (prev: DayOffTime[], next: RepairShopBusinessBreakTimes) => {
        const { startBreakTime, endBreakTime, breakDayTypes } = next;
        if (breakDayTypes === DayOffDay.SUNDAY)
          return [
            ...prev,
            {
              startTime: stringTimeToMoment(startBreakTime),
              endTime: stringTimeToMoment(endBreakTime),
            },
          ];
        return prev;
      },
      []
    );
    holidayRestTimes.times = repairShopBreakTimes.reduce(
      (prev: DayOffTime[], next: RepairShopBusinessBreakTimes) => {
        const { startBreakTime, endBreakTime, breakDayTypes } = next;
        if (breakDayTypes === DayOffDay.HOLIDAY)
          return [
            ...prev,
            {
              startTime: stringTimeToMoment(startBreakTime),
              endTime: stringTimeToMoment(endBreakTime),
            },
          ];
        return prev;
      },
      []
    );

    switch (isEveryDayRestSame) {
      case BusinessDayType.EVERYDAY:
        for (let i = 0; i < weekBreakTime.length; i += 1) {
          const nowBreakTime = weekBreakTime[i];
          everyDaySameRestTimeArray.push(nowBreakTime);
        }
        break;
      case BusinessDayType.WEEKDAY_WEEKEND:
        for (let i = 0; i < weekBreakTime.length; i += 1) {
          const nowBreakTime = weekBreakTime[i];
          notEveryDaySameRestTimeArray[0].times.push(nowBreakTime);
        }
        for (let i = 0; i < weekendBreakTime.length; i += 1) {
          const nowBreakTime = weekendBreakTime[i];
          notEveryDaySameRestTimeArray[1].times.push(nowBreakTime);
        }
        break;
      case BusinessDayType.DAY: {
        for (let i = 0; i < repairShopBreakTimes.length; i += 1) {
          const nowBreakTime = repairShopBreakTimes[i];
          const {
            groupIndex = 0,
            breakDayTypes = DayOffDay.MONDAY,
            startBreakTime,
            endBreakTime,
          } = nowBreakTime;
          if (!alldayBreakTimeArray[groupIndex])
            alldayBreakTimeArray[groupIndex] = {
              days: [breakDayTypes],
              breakTimes: [{ startBreakTime, endBreakTime }],
            };
          else if (
            !alldayBreakTimeArray[groupIndex].days.includes(breakDayTypes)
          ) {
            alldayBreakTimeArray[groupIndex].days.push(breakDayTypes);
          } else if (
            alldayBreakTimeArray[groupIndex].breakTimes.find(
              (val) =>
                val.startBreakTime === startBreakTime &&
                val.endBreakTime === endBreakTime
            ) === undefined
          )
            alldayBreakTimeArray[groupIndex].breakTimes.push({
              startBreakTime,
              endBreakTime,
            });
        }
        break;
      }
      default:
        break;
    }
  }
  // 만약 값이 없을 때 넣을 초기값
  for (let i = 0; i < notEveryDaySameRestTimeArray.length; i += 1) {
    if (notEveryDaySameRestTimeArray[i].times.length === 0)
      notEveryDaySameRestTimeArray[i].times.push(initialTime);
  }
  // 만약 값이 없을 때 넣을 초기값
  if (everyDaySameRestTimeArray.length === 0)
    everyDaySameRestTimeArray.push(initialTime);
  // 만약 값이 없을 때 넣을 초기값
  if (holidayRestTimes.times.length === 0)
    holidayRestTimes.times.push(initialTime);

  if (alldayBreakTimeArray.length === 0)
    alldayBreakTimeArray.push({
      days: [],
      breakTimes: [{ endBreakTime: undefined, startBreakTime: undefined }],
    });
  // 만듬 값들을 formvalue 로 넣음
  const configFormValues: ConfigFormValues = {
    isEveryDayRestSame,
    alldayBreakTimeArray,
    isWorkAtHoliday,
    everyDaySameRestTimeArray,
    notEveryDaySameRestTimeArray,
    holidayRestTimes,
  };
  return configFormValues;
};
// 초기영업시간
export const getInitialBusinessTime = (
  repairShopBusiness: NewRepairShopBusiness
) => {
  const {
    businessDayTypes = BusinessDayType.EVERYDAY,
    mondayStartBusinessTime,
    mondayEndBusinessTime,
    sundayEndBusinessTime,
    sundayStartBusinessTime,
  } = repairShopBusiness;

  // 시간을 분해한 것을 담은 배열 "00:00" => ["00","00" ]
  // ㅡmoment일 경우에는 format 해서 담음

  // 매일같은지 주말주중다른지 매일다름  체크해서 맞는 시간상태에 담음
  // startTime,endTime: 매일 같음, weekdayWeekendTimeArray:주말 주중 다름 , allDay: 매일 일하는 시간이 다름
  const configFormValues: ConfigFormValues = {
    startTime:
      businessDayTypes === BusinessDayType.EVERYDAY
        ? stringTimeToMoment(mondayStartBusinessTime || undefined)
        : undefined,
    endTime:
      businessDayTypes === BusinessDayType.EVERYDAY
        ? stringTimeToMoment(mondayEndBusinessTime || undefined)
        : undefined,
    weekdayWeekendTimeArray: [
      {
        startTime:
          businessDayTypes === BusinessDayType.WEEKDAY_WEEKEND
            ? stringTimeToMoment(mondayStartBusinessTime || undefined)
            : undefined,
        endTime:
          businessDayTypes === BusinessDayType.WEEKDAY_WEEKEND
            ? stringTimeToMoment(mondayEndBusinessTime || undefined)
            : undefined,
        days: ["평일"],
      },
      {
        startTime:
          businessDayTypes === BusinessDayType.WEEKDAY_WEEKEND
            ? stringTimeToMoment(sundayStartBusinessTime || undefined)
            : undefined,
        endTime:
          businessDayTypes === BusinessDayType.WEEKDAY_WEEKEND
            ? stringTimeToMoment(sundayEndBusinessTime || undefined)
            : undefined,
        days: ["주말"],
      },
    ],

    alldayTimeArray:
      businessDayTypes === BusinessDayType.DAY
        ? getInitialAlldayTimeArray(repairShopBusiness)
        : [
            {
              startTime: undefined,
              endTime: undefined,
              days: initialDayOffDays,
            },
            {
              startTime: undefined,
              endTime: undefined,
              days: [DayOffDay.SATURDAY, DayOffDay.SUNDAY],
            },
          ],
  };

  return configFormValues;
};
interface DayInfo {
  startTime?: string | null;
  endTime?: string | null;
  day?: DayOffDay;
}
// 매일 다른 시간에 일할 때 배열 만드는 함수
export const getInitialAlldayTimeArray = (
  repairShopBusiness: NewRepairShopBusiness
) => {
  const {
    mondayEndBusinessTime,
    tuesdayEndBusinessTime,
    wednesdayEndBusinessTime,
    thursdayEndBusinessTime,
    fridayEndBusinessTime,
    saturdayEndBusinessTime,
    sundayEndBusinessTime,
    mondayStartBusinessTime,
    tuesdayStartBusinessTime,
    wednesdayStartBusinessTime,
    thursdayStartBusinessTime,
    fridayStartBusinessTime,
    saturdayStartBusinessTime,
    sundayStartBusinessTime,
  } = repairShopBusiness;
  const reservationTimeArray: ReservationTime[] = [];
  const dayInfoArray: DayInfo[] = [];
  // 함수안의 함수 => 요일을 확인해서 시간상태를 만든후 배열에 집어넣음
  const makeNewDayInfoArray = (dayOffDay?: DayOffDay) => {
    const newDayInfo: DayInfo = {
      startTime: undefined,
      endTime: undefined,
      day: undefined,
    };
    if (dayOffDay === DayOffDay.MONDAY) {
      newDayInfo.startTime = mondayStartBusinessTime;
      newDayInfo.endTime = mondayEndBusinessTime;
    }
    if (dayOffDay === DayOffDay.TUESDAY) {
      newDayInfo.startTime = tuesdayStartBusinessTime;
      newDayInfo.endTime = tuesdayEndBusinessTime;
    }
    if (dayOffDay === DayOffDay.WEDNESDAY) {
      newDayInfo.startTime = wednesdayStartBusinessTime;
      newDayInfo.endTime = wednesdayEndBusinessTime;
    }
    if (dayOffDay === DayOffDay.THURSDAY) {
      newDayInfo.startTime = thursdayStartBusinessTime;
      newDayInfo.endTime = thursdayEndBusinessTime;
    }
    if (dayOffDay === DayOffDay.FRIDAY) {
      newDayInfo.startTime = fridayStartBusinessTime;
      newDayInfo.endTime = fridayEndBusinessTime;
    }
    if (dayOffDay === DayOffDay.SATURDAY) {
      newDayInfo.startTime = saturdayStartBusinessTime;
      newDayInfo.endTime = saturdayEndBusinessTime;
    }
    if (dayOffDay === DayOffDay.SUNDAY) {
      newDayInfo.startTime = sundayStartBusinessTime;
      newDayInfo.endTime = sundayEndBusinessTime;
    }
    newDayInfo.day = dayOffDay;
    dayInfoArray.push(newDayInfo);
  };
  for (let i = 0; i < dayOffDays.length; i += 1) {
    const dayOffDay = dayOffDays[i];
    makeNewDayInfoArray(dayOffDay);
  }
  // 앞에서 만든 배열에서  시간을 확인해서 그 요일이 같은 시간에 일하는 경우 같은 배열로 묶는다 ex) 월,화:9:00~10:00 이고 수,목,금 :00:00 인경우 [월,화],[수,목,금] 으로 묶는다.
  for (let i = 0; i < dayInfoArray.length; i += 1) {
    let isInArray = true;
    const nowInfo: DayInfo = dayInfoArray[i];
    const { day = DayOffDay.MONDAY, endTime, startTime } = nowInfo;
    if (reservationTimeArray.length === 0)
      reservationTimeArray.push({
        startTime: startTime === null ? undefined : moment(startTime),
        endTime: endTime === null ? undefined : moment(endTime),
        days: [day],
      });
    else {
      for (let j = 0; j < reservationTimeArray.length; j += 1) {
        const nowReservationTime = reservationTimeArray[j];
        if (
          moment(startTime).format("HH:mm") ===
            nowReservationTime.startTime?.format("HH:mm") &&
          moment(endTime).format("HH:mm") ===
            nowReservationTime.endTime?.format("HH:mm")
        ) {
          reservationTimeArray[j].days?.push(day);
          isInArray = false;
          break;
        }
      }
      if (isInArray)
        reservationTimeArray.push({
          startTime: startTime === null ? undefined : moment(startTime),
          endTime: endTime === null ? undefined : moment(endTime),
          days: [day],
        });
    }
  }

  return reservationTimeArray.filter(
    (val) => val.endTime !== undefined || val.startTime !== undefined
  );
};

// 서버에서 받아온 repairshopValue를 formvalue로 바꾼다
export const changeRepairShopValueToFormValue = (
  repairShopBusiness: RepairShopBusiness
) => {
  const value = repairShopBusiness || initialRepairshopBusiness;

  const keys = Object.keys(value);
  // 요일의 키값을 담은 배열
  const momentKeys = repairShopBusinessMomentKeys;
  const newRepairShopBusiness: NewRepairShopBusiness = { ...value };
  // 시간을 모멘트함 공휴일인경우 HH:mm으로 포맷팅함
  for (let i = 0; i < keys?.length; i += 1) {
    const key = keys[i];

    const businessValue = newRepairShopBusiness[key];
    if (businessValue) {
      if (momentKeys.includes(key)) {
        const splittedBusinessValue = businessValue?.split(":");
        if (splittedBusinessValue?.length === 0) {
          newRepairShopBusiness[key] = moment().hours(0).minutes(0);
        } else
          newRepairShopBusiness[key] = moment()
            .hours(splittedBusinessValue[0])
            .minutes(splittedBusinessValue[1]);
      }
      if (holidayTimes.includes(key)) {
        const splittedBusinessValue = businessValue?.split(":");
        newRepairShopBusiness[key] = moment()
          .hours(splittedBusinessValue[0])
          .minutes(splittedBusinessValue[1])
          .format("HH:mm");
      }
    }
  }

  return {
    ...newRepairShopBusiness,
  };
};
// 각 타입에 맞는 영업시간을 만듬
const makeEverydayBusinessTime = (
  values: ConfigFormValues,
  requestBody: RepairShopBusiness
) => {
  const { startTime, endTime } = values;
  requestBody = parseStringArray(
    startWeekTimes,
    startTime?.format("HH:mm"),
    requestBody
  );
  requestBody = parseStringArray(
    endWeekTimes,
    endTime?.format("HH:mm"),
    requestBody
  );
  requestBody = parseStringArray(
    startWeekendTime,
    startTime?.format("HH:mm"),
    requestBody
  );
  requestBody = parseStringArray(
    endWeekendTime,
    endTime?.format("HH:mm"),
    requestBody
  );

  return requestBody;
};
// 각 타입에 맞는 영업시간을 만듬
const makeWeekAndWeekendBusinessTime = (
  values: ConfigFormValues,
  requestBody: RepairShopBusiness
) => {
  const { weekdayWeekendTimeArray = [] } = values;
  requestBody = parseStringArray(
    startWeekTimes,
    weekdayWeekendTimeArray[0].startTime?.format("HH:mm"),
    requestBody
  );
  requestBody = parseStringArray(
    endWeekTimes,
    weekdayWeekendTimeArray[0].endTime?.format("HH:mm"),
    requestBody
  );
  requestBody = parseStringArray(
    startWeekendTime,
    weekdayWeekendTimeArray[1].startTime?.format("HH:mm"),
    requestBody
  );
  requestBody = parseStringArray(
    endWeekendTime,
    weekdayWeekendTimeArray[1].endTime?.format("HH:mm"),
    requestBody
  );
  return requestBody;
};
// 각 타입에 맞는 영업시간을 만듬
const makeDayBusinessTime = (
  values: ConfigFormValues,
  requestBody: RepairShopBusiness
) => {
  const { alldayTimeArray = [] } = values;
  for (let i = 0; i < alldayTimeArray?.length; i += 1) {
    const days = alldayTimeArray[i]?.days || [];
    const startTime = alldayTimeArray[i]?.startTime
      ? alldayTimeArray[i]?.startTime?.format("HH:mm") || "00:00"
      : null;
    const endTime = alldayTimeArray[i]?.endTime
      ? alldayTimeArray[i]?.endTime?.format("HH:mm") || "00:00"
      : null;
    for (let j = 0; j < days?.length; j += 1) {
      const day = days[j] || DayOffDay.MONDAY;
      switch (day) {
        case DayOffDay.MONDAY:
          requestBody = {
            ...requestBody,
            mondayStartBusinessTime: startTime,
            mondayEndBusinessTime: endTime,
          };
          break;
        case DayOffDay.TUESDAY:
          requestBody = {
            ...requestBody,
            tuesdayStartBusinessTime: startTime,
            tuesdayEndBusinessTime: endTime,
          };
          break;
        case DayOffDay.WEDNESDAY:
          requestBody = {
            ...requestBody,
            wednesdayStartBusinessTime: startTime,
            wednesdayEndBusinessTime: endTime,
          };
          break;
        case DayOffDay.THURSDAY:
          requestBody = {
            ...requestBody,
            thursdayStartBusinessTime: startTime,
            thursdayEndBusinessTime: endTime,
          };
          break;
        case DayOffDay.FRIDAY:
          requestBody = {
            ...requestBody,
            fridayStartBusinessTime: startTime,
            fridayEndBusinessTime: endTime,
          };
          break;
        case DayOffDay.SATURDAY:
          requestBody = {
            ...requestBody,
            saturdayStartBusinessTime: startTime,
            saturdayEndBusinessTime: endTime,
          };
          break;
        case DayOffDay.SUNDAY:
          requestBody = {
            ...requestBody,
            sundayStartBusinessTime: startTime,
            sundayEndBusinessTime: endTime,
          };
          break;
        default:
          requestBody = {
            ...requestBody,
            mondayStartBusinessTime: startTime,
            mondayEndBusinessTime: endTime,
          };
          break;
      }
    }
  }
  return requestBody;
};
const parseStringArray = (array: string[], value: any, target: any) => {
  for (let i = 0; i < array.length; i += 1) {
    target[array[i]] = value || null;
  }
  return target;
};

export const stringTimeToMoment = (
  string?: string | moment.Moment
): moment.Moment | undefined => {
  if (string === undefined || string === null) return undefined;
  if (typeof string === "string") {
    const splittedValue = string?.split(":");
    return moment()
      .hours(Number(splittedValue[0]))
      .minutes(Number(splittedValue[1]));
  }
  return string;
};
export const getTemporaryBusinessInfos = (
  preRequestBody: EditRepairShopBusinessRequestBody,
  temporaryChangedInfo: RepairShopTemporaryChangedBusiness
) => {
  const { repairShopBusiness } = preRequestBody;

  const {
    repairShopBreakTimes = [],
    reservationInterval = ReservationInterval.THIRTY,
  } = repairShopBusiness;

  const newRepairShopBreakTimes =
    temporaryChangedInfo?.repairShopBreakTimes &&
    temporaryChangedInfo?.repairShopBreakTimes?.length > 0
      ? temporaryChangedInfo.repairShopBreakTimes
      : repairShopBreakTimes;

  const businessInfos: BusinessInfoOfDay[] = [];
  for (let day = 0; day <= 7; day += 1) {
    switch (day) {
      case 0:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.SUNDAY,
          reservationInterval,
        };
        break;
      case 1:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.MONDAY,
          reservationInterval,
        };
        break;
      case 2:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.TUESDAY,
          reservationInterval,
        };
        break;
      case 3:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.WEDNESDAY,
          reservationInterval,
        };
        break;
      case 4:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.THURSDAY,
          reservationInterval,
        };
        break;
      case 5:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.FRIDAY,
          reservationInterval,
        };
        break;
      case 6:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.SATURDAY,
          reservationInterval,
        };
        break;
      case 7:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.HOLIDAY,
          reservationInterval,
        };
        break;
      default:
        businessInfos[day] = {
          startTime: temporaryChangedInfo?.startBusinessTime,
          endTime: temporaryChangedInfo?.endBusinessTime,
          breakTimes: newRepairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.HOLIDAY,
          reservationInterval,
        };
        break;
    }
  }

  return businessInfos;
};
export const getBusinessInfos = (
  preRequestBody: EditRepairShopBusinessRequestBody
) => {
  const { repairShopBusiness } = preRequestBody;
  const {
    repairShopBreakTimes = [],
    reservationInterval = ReservationInterval.THIRTY,
  } = repairShopBusiness;
  const businessInfos: BusinessInfoOfDay[] = [];
  for (let day = 0; day <= 7; day += 1) {
    switch (day) {
      case 0:
        businessInfos[day] = {
          startTime: repairShopBusiness.sundayStartBusinessTime,
          endTime: repairShopBusiness.sundayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SUNDAY
          ),
          dayOffDay: DayOffDay.SUNDAY,
          reservationInterval,
        };
        break;
      case 1:
        businessInfos[day] = {
          startTime: repairShopBusiness.mondayStartBusinessTime,
          endTime: repairShopBusiness.mondayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.MONDAY
          ),
          dayOffDay: DayOffDay.MONDAY,
          reservationInterval,
        };
        break;
      case 2:
        businessInfos[day] = {
          startTime: repairShopBusiness.tuesdayStartBusinessTime,
          endTime: repairShopBusiness.tuesdayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.TUESDAY
          ),
          dayOffDay: DayOffDay.TUESDAY,
          reservationInterval,
        };
        break;
      case 3:
        businessInfos[day] = {
          startTime: repairShopBusiness.wednesdayStartBusinessTime,
          endTime: repairShopBusiness.wednesdayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.WEDNESDAY
          ),
          dayOffDay: DayOffDay.WEDNESDAY,
          reservationInterval,
        };
        break;
      case 4:
        businessInfos[day] = {
          startTime: repairShopBusiness.thursdayStartBusinessTime,
          endTime: repairShopBusiness.thursdayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.THURSDAY
          ),
          dayOffDay: DayOffDay.THURSDAY,
          reservationInterval,
        };
        break;
      case 5:
        businessInfos[day] = {
          startTime: repairShopBusiness.fridayStartBusinessTime,
          endTime: repairShopBusiness.fridayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.FRIDAY
          ),
          dayOffDay: DayOffDay.FRIDAY,
          reservationInterval,
        };
        break;
      case 6:
        businessInfos[day] = {
          startTime: repairShopBusiness.saturdayStartBusinessTime,
          endTime: repairShopBusiness.saturdayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.SATURDAY
          ),
          dayOffDay: DayOffDay.SATURDAY,
          reservationInterval,
        };
        break;
      case 7:
        businessInfos[day] = {
          startTime: repairShopBusiness.holidayStartBusinessTime,
          endTime: repairShopBusiness.holidayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.HOLIDAY
          ),
          dayOffDay: DayOffDay.HOLIDAY,
          reservationInterval,
        };
        break;
      default:
        businessInfos[day] = {
          startTime: repairShopBusiness.holidayStartBusinessTime,
          endTime: repairShopBusiness.holidayEndBusinessTime,
          breakTimes: repairShopBreakTimes.filter(
            (val) => val.breakDayTypes === DayOffDay.HOLIDAY
          ),
          dayOffDay: DayOffDay.HOLIDAY,
          reservationInterval,
        };
        break;
    }
  }

  return businessInfos;
};
export const getResevationTimeArray = (
  busiInfo: BusinessInfoOfDay
): CalendarTime[] => {
  const { startTime, endTime, reservationInterval, breakTimes = [] } = busiInfo;
  const reservationTimeArray: CalendarTime[] = [];

  if (startTime === null || endTime === null) return reservationTimeArray;

  const startMoment = stringTimeToMoment(startTime);
  const endMoment = stringTimeToMoment(endTime);
  if (startMoment === undefined || endMoment === undefined)
    return reservationTimeArray;
  reservationTimeArray.push({
    time: startMoment?.format("HH:mm"),
    disabled: false,
  });
  const diffOfTime = moment.duration(endMoment?.diff(startMoment)).asMinutes();
  const getDisabled = (moment: moment.Moment) => {
    const isDisabled = false;
    for (let i = 0; i < breakTimes.length; i += 1) {
      const breakTime = breakTimes[i];

      if (
        moment.isBetween(
          stringTimeToMoment(breakTime.startBreakTime)?.subtract(1, "minute"),
          stringTimeToMoment(breakTime.endBreakTime)?.subtract(1, "minute")
        )
      )
        return true;
    }
    return isDisabled;
  };

  for (let i = 0; i < diffOfTime; i += reservationInterval) {
    const newMoment = startMoment?.add(reservationInterval, "minutes");
    if (startMoment.hours() !== 0 && newMoment.hours() !== 0)
      if (i <= diffOfTime)
        if (!getDisabled(newMoment))
          reservationTimeArray.push({
            time: newMoment.format("HH:mm"),
            disabled: getDisabled(newMoment),
          });
  }

  return reservationTimeArray.filter((val) =>
    stringTimeToMoment(val.time)?.isBefore(endMoment)
  );
};
export const checkDateInvalid = (
  value: moment.Moment[],
  conditions: moment.Moment[][]
) => {
  const invalid = true;

  for (let i = 0; i < value.length; i += 1) {
    const nowMoment = value[i];
    for (let j = 0; j < conditions.length; j += 1) {
      if (nowMoment.isBetween(conditions[j][0], conditions[j][1])) return false;
      if (
        nowMoment?.format("YYYY-MM-DD") ===
        conditions[j][1]?.format("YYYY-MM-DD")
      )
        return false;
    }
  }

  return invalid;
};
